import {
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useUser } from "@/auth/useUser";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
} from "@/components/common/drawer";
import { TOP_BAR_HEIGHT } from "@/components/common/topBar/topBar";
import BuyAsAGiftModal from "@/components/online-store/checkout/buyAsAGiftModal";
import OnlineStoreCheckoutActions from "@/components/online-store/checkout/onlineStoreCheckoutActions";
import PackageCartItem from "@/components/online-store/checkout/packageCartItem";
import useCreatePackageCheckout from "@/components/online-store/checkout/useCreatePackageCheckout";
import { useConfirm } from "@/components/providers/confirmProvider";
import { GREY } from "@/config/mui/colorPalette";
import {
  EventType,
  useOnlineStoreMixpanelEvents,
} from "@/hooks/onlineStore/useOnlineStoreMixpanelEvents";
import usePersistedStore from "@/store/usePersistedStore";
import {
  getReset,
  useGiftEmailCreatorFormStore,
} from "@/store/views/onlineStore/giftEmailCreatorFormStore";
import useOnlineStorePackagesStore from "@/store/views/onlineStore/onlineStorePackagesStore";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";

const GIFTING_EMAIL_BUILDER_MODAL_CONFIG = {
  discardButtonText: "No, just buy",
  confirmButtonText: "Yes, let’s create",
  title: "Do you want to create and send an email to a recipient?",
  description:
    "Alongside the perfect gift, surprise your friend with a personalized email created just for them by you!",
};

export default function OnlineStoreCheckout({
  isDrawerOpen,
  onClose,
  medspaId,
  packagesData,
  customizationEnabled,
  customizationPrimaryColor,
  cherryIntegrationV1Enabled,
}: {
  isDrawerOpen: boolean;
  onClose: () => void;
  medspaId: string;
  packagesData: MedspaPackage[];
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  cherryIntegrationV1Enabled: boolean;
}) {
  const router = useRouter();
  const { trackEvent } = useOnlineStoreMixpanelEvents(medspaId);
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery<typeof theme>((theme) =>
    theme.breakpoints.down("md")
  );

  const { packageId, source } = router.query;

  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);

  const { goToCheckout, isLoading } = useCreatePackageCheckout(
    medspaId,
    packagesData
  );

  const store = useOnlineStorePackagesStore(medspaId);

  const selectedPackages = usePersistedStore(
    store,
    (state) => state.packagesQuantity
  );

  const purchaseSource = usePersistedStore(
    store,
    (state) => state.purchaseSource
  );

  const { setPurchaseSource } = store.getState();

  // If the package was suggested store the package id and source
  useEffect(() => {
    if (packageId && source && !purchaseSource) {
      setPurchaseSource({
        id: packageId as string,
        source: source as string,
      });
    }
  }, [packageId, setPurchaseSource, source, purchaseSource]);

  const totalPackagesQuantity = useMemo(
    () =>
      Object.values(selectedPackages || {}).reduce(
        (acc, curr) => acc + curr,
        0
      ),
    [selectedPackages]
  );

  const canOpenCheckout = useMemo(
    () => Object.values(selectedPackages || {}).length > 0,
    [selectedPackages]
  );

  const totalPrice = useMemo(
    () =>
      Object.entries(selectedPackages || {}).reduce(
        (acc, [packageId, quantity]) => {
          const p = packagesData.find((p) => p.id === packageId);
          if (!p) return acc;
          return acc + +p.packagePrice * quantity;
        },
        0
      ),
    [selectedPackages, packagesData]
  );

  const packagesInCheckout = useMemo(() => {
    if (!selectedPackages) return [];

    return packagesData.filter((p) => {
      const quantity = selectedPackages[p.id];
      return quantity !== undefined;
    });
  }, [selectedPackages, packagesData]);

  useEffect(() => {
    if (!canOpenCheckout) {
      onClose();
    }
  }, [canOpenCheckout, onClose]);

  const { getConfirm } = useConfirm();
  const {
    push,
    query: { medspaSlug },
  } = useRouter();
  const resetForm = useGiftEmailCreatorFormStore(getReset);

  const handleBuyAsGift = async () => {
    const shouldCreateEmail = await getConfirm({
      ...GIFTING_EMAIL_BUILDER_MODAL_CONFIG,
      customTheme: theme,
    });
    if (shouldCreateEmail) {
      resetForm();
      return push(`/online-store/${medspaSlug}/gift-email-creator`);
    }
    trackEvent(EventType.BUY_AS_STANDARD_GIFT_CLICKED);
    setIsGiftModalOpen(true);
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={onClose}
      ModalProps={{
        sx: {
          pr: 2,
          "& .MuiDrawer-paper": {
            mt: isMobile ? 0 : TOP_BAR_HEIGHT,
            width: isMobile ? "100%" : "500px",
            mr: -2,
          },
        },
      }}
    >
      <DrawerHeader
        HeaderProps={{
          sx: {
            mt: user && !isMobile ? `-${TOP_BAR_HEIGHT}` : 0,
          },
        }}
        title={
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitleDefault">Your cart • </Typography>
            <Typography variant="paragraphSmall">
              {totalPackagesQuantity} item{totalPackagesQuantity > 1 ? "s" : ""}
            </Typography>
          </Stack>
        }
        CloseIconButtonProps={{
          sx: {
            bgcolor: GREY[5],
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            ml: -4,
          },
        }}
        CloseActionBoxProps={{
          sx: {
            ml: "auto",
            mr: isMobile ? 0 : 2,
          },
        }}
        onCloseAction={onClose}
      />
      <DrawerContent>
        <Stack
          divider={<Divider sx={{ my: 2 }} />}
          sx={{
            mb: isMobile ? -2 : 2,
            pr: isMobile ? 0 : 2,
          }}
        >
          {packagesInCheckout.map((p) => (
            <PackageCartItem
              key={p.id}
              packageItem={p}
              medspaId={medspaId}
              customizationEnabled={customizationEnabled}
              customizationPrimaryColor={customizationPrimaryColor}
            />
          ))}
        </Stack>
      </DrawerContent>
      <Stack
        sx={{
          p: 2,
          pr: isMobile ? 2 : 4,
        }}
      >
        <Divider sx={{ mt: 0.5 }} />
        <OnlineStoreCheckoutActions
          totalPrice={totalPrice}
          isLoading={isLoading}
          goToCheckout={() => goToCheckout()}
          isMobile={isMobile}
          onGiftModalOpen={handleBuyAsGift}
          cherryIntegrationV1Enabled={cherryIntegrationV1Enabled}
        />
      </Stack>
      <BuyAsAGiftModal
        medspaId={medspaId}
        packagesData={packagesData}
        isOpen={isGiftModalOpen}
        onClose={() => setIsGiftModalOpen(false)}
      />
    </Drawer>
  );
}
